import { ConnectionState, RealTimeEvent, RealTimeResource, subscribeToResource } from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';

interface UseRealTimeResourceOptions {
  resource: RealTimeResource | undefined;
  id: string | undefined;
  /** Set to false to keep the hook mounted without a subscription (e.g. while the resource is still loading). */
  enabled?: boolean;
  /** Called on every real-time event (including `connection.resumed`). Consumers refetch their data here. */
  onEvent: (event: RealTimeEvent) => void;
}

/**
 * Subscribes to the real-time updates of one resource for as long as the component is mounted.
 * The subscription is (re)opened when `resource`, `id` or `enabled` change, never when `onEvent` changes.
 */
export const useRealTimeResource = ({ resource, id, enabled = true, onEvent }: UseRealTimeResourceOptions) => {
  const [state, setState] = useState<ConnectionState>('closed');

  // Always call the latest handler without making it a dependency of the subscription effect.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !id || !resource) {
      setState('closed');
      return;
    }

    let active = true;
    const subscription = subscribeToResource({
      resource,
      id,
      onEvent: (event) => onEventRef.current(event),
      onStateChange: (nextState) => {
        if (active) setState(nextState);
      },
    });

    return () => {
      active = false;
      subscription.close();
    };
  }, [resource, id, enabled]);

  return { state, live: state === 'open' };
};
